import {
  type Cart as CartData,
  CartType,
} from "@cappelendamm/e-commerce-services";
import { isItemDigitalStringComparison } from "@/products/selectors/isItemDigital";

export const getCartNumberOfItems = (cart: CartData | null) =>
  getNumberOfItems(cart?.items.filter((item) => !isFreightItem(item)) ?? []);

export const getCartTotalDiscount = (
  cart: CartData | null,
): {
  total: number | null;
  totalDiscountProducts: number | null;
  originalTotalProducts: number | null;
} => {
  if (!cart)
    return {
      total: null,
      totalDiscountProducts: null,
      originalTotalProducts: null,
    };
  const productItems = cart.items.filter((item) => !isFreightItem(item));
  const originalTotal = productItems.reduce((acc, curr) => {
    const priceOfCurrentItem =
      curr.variant.compareAtPrice?.gross ?? curr.variant.price.gross;
    return acc + priceOfCurrentItem * curr.quantity;
  }, 0);
  const discountedProductTotal = productItems.reduce(
    (acc, curr) => acc + curr.price.gross,
    0,
  );
  const totalDiscountProducts =
    originalTotal > discountedProductTotal
      ? originalTotal - discountedProductTotal
      : null;
  return {
    total: cart.total.gross,
    originalTotalProducts: originalTotal,
    totalDiscountProducts,
  };
};

export const isFreightItem = (item: CartData["items"][number]) =>
  item.variant.product.path.startsWith("/pim/fraktregler");

export const isDigitalServiceItem = (item: CartData["items"][number]) =>
  item.variant.product.path.startsWith("/pim/digitale-tjenester");

export const isDigitalCartItem = (item: CartData["items"][number]) => {
  if (isDigitalServiceItem(item)) return true;

  const binding = item.variant.attributes?.Innbinding;
  return !!binding && isItemDigitalStringComparison(binding);
};

export const cartHasPhysicalItems = (cart: CartData) =>
  cart.items.some((item) => !isFreightItem(item) && !isDigitalCartItem(item));

export const getCartFreightPrice = (cart: CartData | null) => {
  const freightItems = cart?.items.filter(isFreightItem);
  if (!freightItems || freightItems.length === 0) return null;
  const price = freightItems.reduce((acc, cur) => {
    return acc + cur.price.gross;
  }, 0);
  const discount = freightItems.reduce<null | number>((acc, cur) => {
    const d = cur.price.discounts?.reduce((a, c) => a + c.amount, 0);
    if (typeof d === "number") return (acc ?? 0) + d;
    return null;
  }, null);
  return { price, discount };
};

export const getNumberOfItems = (items: { quantity: number }[]) =>
  items.reduce((acc, cur) => acc + cur.quantity, 0) ?? 0;

export const getCustomerGroupIdentifiers = (
  parents: { type: string; identifier: string }[] | null | undefined,
): string[] =>
  parents
    ?.filter((parent) => parent.type === "customerGroup")
    .map((parent) => parent.identifier) ?? [];

export const hasVATProductInCart = (cart: CartData | null) =>
  cart?.items.some(
    (item) => item.variant.price.gross !== item.variant.price.net,
  ) ?? false;

/**
 * A cart is stale when Crystallize has passed its `staleAt` deadline. Stale carts
 * are moved to `abandoned` within one hour, and Crystallize only applies
 * promotions (e.g. free shipping) to carts in the `cart` state, so prices read
 * from a stale cart can be wrong.
 *
 * `staleAt` is only pushed forward on writes, never on reads.
 * Placed and ordered carts are never considered stale.
 */
export const isCartStale = (cart: CartData) =>
  cart.state === "abandoned" ||
  (cart.state === "cart" && cart.isStale === true);

/**
 * An organization cart must be checked out with an active organization.
 * Without one (e.g. an expired session at submit time) checkout would fall
 * back to the guest flow and place the order on a new customer instead of the
 * organization.
 */
export const isOrganizationCartWithoutActiveOrganization = (
  cart: CartData,
  activeOrganizationNumber: string | undefined,
) => cart.cartType === CartType.ORGANIZATION && !activeOrganizationNumber;

export const isUnclaimedGuestCart = (cart: CartData) =>
  cart.state === "cart" &&
  cart.cartType !== CartType.ORGANIZATION &&
  cart.customer?.isGuest !== false;
